/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import Placeholder from 'src/components/Dashboard/Placeholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import HTTP404 from 'src/components/HTTP404';
import Loading from 'src/components/Loading';
import { PortfolioShow } from 'src/components/Portfolio';
import { APIConfig } from 'src/config';
import { INHIBIT_PORTFOLIO_VIEWS } from 'src/util/constants';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getRouteDateParams from './helpers/getRouteDateParams';
import getShowRouteKey from './helpers/getShowRouteKey';
import portfolioShowParams from './helpers/portfolio';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const portfolioShowRouteQuery = graphql`
  query PortfolioShowRoute_Query(
    $uuid: ID!
  ) {
      ...PortfolioShow
        @arguments(
          uuid: $uuid
        )
  }
`;

const portfolioShowRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    location: PropTypes.shape({
      query: PropTypes.shape({
        aggregateBy: PropTypes.string,
        groupBy: PropTypes.string,
        source: PropTypes.string,
        tz: PropTypes.string,
        unit: PropTypes.string,
        xAxisDomain: PropTypes.string,
      }),
    }),
    params: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
    }),
  }).isRequired,
});

const queryParamsPropShape = PropTypes.shape({
  aggregateBy: PropTypes.string,
  groupBy: PropTypes.string,
  source: PropTypes.string,
  timezone: PropTypes.string,
  unit: PropTypes.string,
  xAxisDomain: PropTypes.string,
});

/**
 * Renders `PortfolioShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @param {object} props.queryParams - Portfolio view params derived from the route query.
 * @returns {React.ReactNode} portfolio show route content.
 */
function PortfolioShowPreloadedQueryRenderer({
  routeProps,
  queryRef,
  queryParams,
}) {
  const queryData = usePreloadedQuery(portfolioShowRouteQuery, queryRef);

  return (
    <PortfolioShow
      {...routeProps}
      {...queryData}
      queryParams={queryParams}
    />
  );
}

PortfolioShowPreloadedQueryRenderer.propTypes = {
  routeProps: portfolioShowRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
  queryParams: queryParamsPropShape.isRequired,
};

/**
 * Hook-safe renderer for the `PortfolioShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * The `INHIBIT_PORTFOLIO_VIEWS` kill-switch is resolved here rather than in the
 * route function so the 404 guard holds uniformly across the resolved, retry
 * and error paths (on the error path found-relay supplies no props).
 * Caller MUST mount this with `key={getShowRouteKey(routeProps)}` so a uuid or
 * query-param change disposes the stale `useQueryLoader` query.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} portfolio show route content.
 */
function PortfolioShowRouteRenderer({ routeProps, routeError = null }) {
  const pageProps = {
    ...routeProps,
    dateParams: getRouteDateParams(routeProps?.match?.location),
  };
  const queryParams = portfolioShowParams(routeProps);
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    portfolioShowRouteQuery,
    routeProps,
    { logLabel: 'PortfolioShowRoute' },
  );

  const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);
  if (inhibitPortfolioViews) {
    return <HTTP404 />;
  }

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={Placeholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <PortfolioShowPreloadedQueryRenderer
            routeProps={pageProps}
            queryRef={queryRef}
            queryParams={queryParams}
          />
        ) : (
          <PortfolioShow
            {...pageProps}
            queryParams={queryParams}
          />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PortfolioShowRouteRenderer.propTypes = {
  routeProps: portfolioShowRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the PortfolioShow route state supplied by found-relay.
 * Any network error is forwarded to the renderer's `RouteErrorThrower` bridge
 * so the local error boundary can catch it. The `INHIBIT_PORTFOLIO_VIEWS`
 * kill-switch is resolved inside `PortfolioShowRouteRenderer` so the 404 guard
 * holds on the error path too. The renderer is keyed on the route uuid and its
 * query params so navigation between portfolios — or a query-param change on the
 * same portfolio — remounts it and disposes the stale `useQueryLoader` query,
 * re-arming the error bridge.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} PortfolioShow route content or loading state.
 */
export function portfolioShowRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <PortfolioShowRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <PortfolioShowRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default PortfolioShowRouteRenderer;
